import { Button, Icon, TIconName } from '@unmind/design-system';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getChannel } from 'utils/getChannel';
import { OnboardingSupportOption } from '__generated__/globalTypes';
import { getSupportOptionConfig } from './supportOptions';
import { useScrollToTopOnChange } from './useScrollToTopOnChange';
import './QuestionView.scss';

/** Artwork for the modal's media panel while the question is shown. */
export { default as QUESTION_VIEW_IMAGE } from './assets/welcome-modal-question.jpg';

/** The modal points `labelledBy` at this, so the dialog has an accessible name. */
export const QUESTION_VIEW_HEADING_ID = 'welcome-modal-question-heading';

export interface QuestionViewProps {
  /** In the order the server returned them. */
  options: OnboardingSupportOption[];
  firstName?: string | null;
  onSelectOption(option: OnboardingSupportOption): void;
  onNotSure(): void;
}

interface OptionRowProps {
  icon: TIconName;
  label: string;
  onClick(): void;
}

/** Shared by the offerings and the "I'm not sure yet" fallback. */
const OptionRow = ({ icon, label, onClick }: OptionRowProps) => (
  <Button
    className="welcome-modal-question__option"
    variant="secondary"
    styling="full-width"
    icon={{ icon: 'chevron-right', position: 'right' }}
    onClick={onClick}
  >
    <span className="welcome-modal-question__option-content">
      <Icon icon={icon} size="large" aria-hidden />
      <span className="welcome-modal-question__option-label">{label}</span>
    </span>
  </Button>
);

/**
 * The question, and a row per offering the member is eligible for.
 *
 * Returns content rather than a `ModalContent`: the design system matches its
 * regions by element type, so the caller composes them.
 */
export const QuestionView = ({
  options,
  firstName,
  onSelectOption,
  onNotSure,
}: QuestionViewProps) => {
  const { t: translate } = useTranslation('homepage', {
    keyPrefix: 'welcome_modal',
  });
  const { productName } = getChannel();
  const rootRef = useRef<HTMLDivElement>(null);

  // Remounts on every return from an offering, so a fixed key is enough.
  useScrollToTopOnChange(rootRef, 'question');

  const shownOptions = options
    .map(option => ({ option, config: getSupportOptionConfig(option) }))
    .filter(
      (
        entry,
      ): entry is {
        option: OnboardingSupportOption;
        config: NonNullable<typeof entry.config>;
      } => entry.config !== undefined,
    );

  return (
    <div className="welcome-modal-question" ref={rootRef}>
      <h2
        id={QUESTION_VIEW_HEADING_ID}
        className="title-xl welcome-modal-question__heading"
      >
        {firstName
          ? translate('question.heading', { firstName })
          : translate('question.heading_without_name')}
      </h2>
      <p className="body-m welcome-modal-question__intro">
        {translate('question.intro')}
      </p>

      <div className="welcome-modal-question__options">
        {shownOptions.map(({ option, config }) => (
          <OptionRow
            key={option}
            icon={config.icon}
            label={translate(`options.${config.translationKey}`)}
            onClick={() => onSelectOption(option)}
          />
        ))}

        <OptionRow
          icon="help"
          label={translate('options.not_sure')}
          onClick={onNotSure}
        />
      </div>

      <p className="body-xs welcome-modal-question__confidentiality">
        <Icon icon="eye-slash" size="small" aria-hidden />
        {translate('question.confidentiality', { productName })}
      </p>
    </div>
  );
};
